import React, { useState, useEffect } from "react";
import {
  Building2,
  Settings,
  Check,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  Loader2,
  Lock
} from "lucide-react";
import { upsertTenantOnboarding, fetchTenantData } from "../lib/userAdminData";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import pms from "../services/pms";
import { showToast } from "../constants";

/** Property shape returned by pms.fetchProperties(). */
interface PmsProperty {
    id: string;
    name?: string;
}

/** Module feature flags written into tenants.data.features. */
type SelectedModules = {
    kassenbuch: boolean;
    hr: boolean;
    hk: boolean;
    ht: boolean;
    fundsachen: boolean;
    xrechnung: boolean;
    traces: boolean;
};

/** Payload handed back via onComplete (mirrors what is written to tenants.data). */
interface OnboardingConfig {
    tenantId?: string;
    allowedProperties: string[];
    features: SelectedModules;
    onboardingCompleted: boolean;
}

/**
 * Onboarding step-by-step assistant for new tenants.
 * Configures basic details, properties, modules, and role configurations.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.tenantId - The target tenant identifier to onboard.
 * @param {Function} props.onComplete - Callback function triggered after final step completion.
 * @returns {JSX.Element} The onboarding step flow.
 */
export default function OnboardingWizard({ tenantId, onComplete }: {
    tenantId: string;
    onComplete: (config: OnboardingConfig) => void;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loadingProps, setLoadingProps] = useState(true);
  const [saving, setSaving] = useState(false);

  // Available properties from Apaleo
  const [availableProps, setAvailableProps] = useState<PmsProperty[]>([]);

  // Selected properties & modules
  const [selectedProps, setSelectedProps] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState({
    kassenbuch: true,
    hr: true,
    hk: true,
    ht: true,
    fundsachen: true,
    xrechnung: true,
    traces: true
  });

  useEffect(() => {
    const loadProps = async () => {
      try {
        const props = await pms.fetchProperties();
        setAvailableProps(props);
        // Pre-select all by default
        setSelectedProps(props.map((p: PmsProperty) => p.id));
      } catch (err) {
        console.error("Failed to load properties for onboarding:", err);
        showToast(`${t('common.error')}: Apaleo-Properties`, "error");
      } finally {
        setLoadingProps(false);
      }
    };
    loadProps();
  }, [t]);

  const toggleProperty = (id: string) => {
    setSelectedProps(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleModule = (moduleKey: keyof SelectedModules) => {
    setSelectedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleFinish = async () => {
    if (selectedProps.length === 0) {
      showToast(t('onboarding.noProperties'), "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        allowedProperties: selectedProps,
        features: selectedModules,
        onboardingCompleted: true
      };
      // Merge into the existing tenant data instead of overwriting it, so
      // unrelated fields (customIframeUrl, propertyOfferEmails, automations,
      // subscriptionStatus, …) are preserved when onboarding is re-run.
      const { data: tenantRow } = await fetchTenantData(tenantId);
      const mergedData = { ...(tenantRow?.data || {}), ...payload };
      await upsertTenantOnboarding(tenantId, mergedData);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      showToast(t('onboarding.complete'), "success");
      onComplete({ tenantId, ...payload });
    } catch (err) {
      console.error("Failed to save onboarding config:", err);
      showToast(`${t('common.error')}: ${err instanceof Error ? err.message : String(err)}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 animate-in fade-in">
      <div className="draggable-modal bg-white border border-slate-200/80 rounded-[2.5rem] shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95">

        {/* Header */}
        <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#859F4A]/10 text-[#859F4A] flex items-center justify-center shadow-inner">
              <Sparkles size={24} />
            </div>
            <div>
              <h2 className="font-black text-2xl text-slate-800 display-font leading-tight">
                {t("onboarding.title")}
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                {t("onboarding.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-1.5 bg-slate-50 border border-slate-100 rounded-2xl">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-8 h-8 rounded-xl font-black text-xs flex items-center justify-center transition-all ${step === s
                  ? "bg-[#859F4A] text-white shadow-md"
                  : step > s
                    ? "bg-[#859F4A]/15 text-[#859F4A]"
                    : "text-slate-400"
                  }`}
              >
                {step > s ? <Check size={14} /> : s}
              </div>
            ))}
          </div>
        </div>

        {/* Steps Content */}
        <div className="flex-1 overflow-y-auto bg-slate-50/50 p-8 space-y-6">
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in">
              <div>
                <h3 className="text-lg font-black text-slate-800 uppercase tracking-wider display-font">
                  {t("onboarding.step1_title")}
                </h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">
                  {t("onboarding.step1_desc")}
                </p>
              </div>

              {loadingProps ? (
                <div className="py-20 flex flex-col items-center justify-center gap-3">
                  <Loader2 size={32} className="animate-spin text-[#859F4A]" />
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {t('onboarding.loadingProperties')}
                  </p>
                </div>
              ) : availableProps.length === 0 ? (
                <div className="p-8 bg-amber-50 border border-amber-100 rounded-3xl text-amber-800 text-xs font-bold leading-relaxed">
                  {t('onboarding.noProperties')}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableProps.map((p) => {
                    const active = selectedProps.includes(p.id);
                    return (
                      <div
                        key={p.id}
                        onClick={() => toggleProperty(p.id)}
                        className={`p-6 rounded-4xl border transition-all duration-300 cursor-pointer flex justify-between items-center ${active
                          ? "bg-white border-[#859F4A] shadow-lg shadow-[#859F4A]/5 scale-102"
                          : "bg-white/60 border-slate-200/60 hover:bg-white hover:border-slate-300"
                          }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#859F4A]/10 text-[#859F4A]" : "bg-slate-100 text-slate-500"}`}>
                            <Building2 size={20} />
                          </div>
                          <div>
                            <p className="text-xs font-black text-slate-800 uppercase tracking-wider">{p.name || p.id}</p>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">ID: {p.id}</p>
                          </div>
                        </div>
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all border ${active ? "bg-[#859F4A] border-[#859F4A] text-white" : "border-slate-200"}`}>
                          {active && <Check size={14} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in fade-in">
              <div>
                <h3 className="text-lg font-black text-slate-800 uppercase tracking-wider display-font">
                  {t("onboarding.step2_title")}
                </h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">
                  {t("onboarding.step2_desc")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "traces", name: t('onboarding.moduleNameTraces'), desc: t('onboarding.moduleDescTraces') },
                  { key: "kassenbuch", name: t('onboarding.moduleNameKassenbuch'), desc: t('onboarding.moduleDescKassenbuch') },
                  { key: "hr", name: t('onboarding.moduleNameHr'), desc: t('onboarding.moduleDescHr') },
                  { key: "hk", name: t('onboarding.moduleNameHk'), desc: t('onboarding.moduleDescHk') },
                  { key: "ht", name: t('onboarding.moduleNameHt'), desc: t('onboarding.moduleDescHt') },
                  { key: "fundsachen", name: t('onboarding.moduleNameFundsachen'), desc: t('onboarding.moduleDescFundsachen') },
                  { key: "xrechnung", name: t('onboarding.moduleNameXrechnung'), desc: t('onboarding.moduleDescXrechnung') },
                ].map((m) => {
                  const active = selectedModules[m.key as keyof SelectedModules];
                  return (
                    <div
                      key={m.key}
                      onClick={() => toggleModule(m.key as keyof SelectedModules)}
                      className={`p-6 rounded-4xl border transition-all duration-300 cursor-pointer flex justify-between items-center ${active
                        ? "bg-white border-[#859F4A] shadow-lg shadow-[#859F4A]/5 scale-102"
                        : "bg-white/60 border-slate-200/60 hover:bg-white hover:border-slate-300"
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#859F4A]/10 text-[#859F4A]" : "bg-slate-100 text-slate-500"}`}>
                          <Settings size={20} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-slate-800 uppercase tracking-wider">{m.name}</p>
                          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight mt-0.5 leading-relaxed">{m.desc}</p>
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all border ${active ? "bg-[#859F4A] border-[#859F4A] text-white" : "border-slate-200"}`}>
                        {active && <Check size={14} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 animate-in fade-in">
              <div>
                <h3 className="text-lg font-black text-slate-800 uppercase tracking-wider display-font">
                  {t("onboarding.step3_title")}
                </h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">
                  {t("onboarding.step3_desc")}
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-8 rounded-4xl shadow-xs space-y-6">
                <div className="space-y-3">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('onboarding.activeLocations')} ({selectedProps.length})</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedProps.map((p) => (
                      <span key={p} className="px-4 py-1.5 bg-slate-50 text-slate-700 border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-wider">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-100">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('onboarding.activeModules')}</span>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.keys(selectedModules).filter((m: string) => selectedModules[m as keyof SelectedModules]).map((m) => (
                      <div key={m} className="flex items-center gap-2 p-3 bg-[#859F4A]/5 text-[#859F4A] rounded-xl text-[10px] font-black uppercase tracking-wider border border-[#859F4A]/10">
                        <CheckCircle size={14} />
                        <span>{m}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-slate-100 bg-white shrink-0 flex justify-between items-center">
          <button
            onClick={() => setStep(prev => prev - 1)}
            disabled={step === 1 || saving}
            className="px-8 py-3.5 text-[10px] font-black text-slate-400 hover:text-[#859F4A] hover:bg-slate-50 rounded-2xl transition-all uppercase tracking-widest disabled:opacity-0 flex items-center gap-2"
          >
            <ArrowLeft size={16} /> {t('onboarding.back')}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(prev => prev + 1)}
              disabled={selectedProps.length === 0}
              className="hub-btn hub-btn-primary group rounded-2xl! px-10! shadow-xl flex items-center gap-2 hover:scale-105 transition-all text-[10px] font-black uppercase tracking-widest"
            >
              <span>{t('onboarding.next')}</span>
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleFinish}
              disabled={saving}
              className="hub-btn hub-btn-primary group rounded-2xl! px-10! shadow-xl flex items-center gap-2 hover:scale-105 transition-all text-[10px] font-black uppercase tracking-widest bg-[#859F4A] text-white shadow-[#859F4A]/25"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
              <span>{t("onboarding.complete")}</span>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
